import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Skeleton, Tag } from 'antd';
import DataTable from '@/components/data-table';
import TruncateTooltipCell from '@/components/data-table/TruncateTooltipCell';
import { Sidebar } from '@/components/sidebar';
import { API } from '@/services/api.service';
import { Facts, ListToolbar, Sums } from './Filters';
import { columnKeysOf, date, listQueryOf, money, noop } from './helpers';
import useColumnHeaders from './useColumnHeaders';

const FILTERS = ['issuedAt', 'number', 'sellerTaxId', 'sellerName', 'invoiceNumber', 'invoiceCreated'];
const SORTS = ['issuedAt', 'number', 'sellerTaxId', 'sellerName', 'totalAmount', 'invoiceNumber'];
const KEYS = columnKeysOf(FILTERS, SORTS);

const dateTime = (v) => (v ? new Date(v).toLocaleString('sr-RS') : '');

/** Fiscal bills the company received (e.g. fuel), as SEF lists them. A row opens the bill. */
export default function FiscalBillsTab({ company, queryParams, setQueryParams }) {
  const { t } = useTranslation('sefState');
  const [selected, setSelected] = useState(null);
  const query = listQueryOf(queryParams);
  const list = useQuery({
    queryKey: ['sefState', company, 'fiscalBills', query],
    queryFn: () => API.sefState.getFiscalBills(company, query),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const { data } = list;
  const h = useColumnHeaders(queryParams, setQueryParams, FILTERS);
  const c = (key) => t(`fiscalBills.columns.${key}`);
  const yesNo = [
    { value: 'true', label: t('status.yes') },
    { value: 'false', label: t('status.no') },
  ];

  const columns = [
    { title: h.date('issuedAt', c('issuedAt')), dataIndex: 'issuedAt', width: 160, render: dateTime },
    { title: h.text('number', c('number')), dataIndex: 'number', width: 250, render: (v) => <TruncateTooltipCell label={v} /> },
    { title: h.text('sellerTaxId', c('sellerTaxId')), dataIndex: 'sellerTaxId', width: 150 },
    { title: h.text('sellerName', c('sellerName')), dataIndex: 'sellerName', width: 300, render: (v) => <TruncateTooltipCell label={v} /> },
    { title: h.sort('totalAmount', c('totalAmount')), dataIndex: 'totalAmount', width: 140, align: 'right', render: money },
    {
      title: h.options('invoiceCreated', t('fiscalBills.invoiceCreated'), yesNo),
      dataIndex: 'invoiceCreated',
      width: 170,
      render: (v) => (v ? <Tag color="blue">{t('status.yes')}</Tag> : ''),
    },
    { title: h.text('invoiceNumber', c('invoiceNumber')), dataIndex: 'invoiceNumber', width: 180, render: (v) => <TruncateTooltipCell label={v} /> },
  ];
  const labels = Object.fromEntries(['issuedAt', 'number', 'sellerTaxId', 'sellerName', 'totalAmount', 'invoiceNumber'].map((k) => [k, c(k)]));

  return (
    <>
      <DataTable
        rowKey="_id"
        title={`${t('fiscalBills.title')}${data ? ` · ${data.count}` : ''}`}
        data={data?.items || []}
        total={data?.count || 0}
        status={list.isLoading ? 'pending' : list.error ? 'error' : 'success'}
        columns={columns}
        offset={queryParams.offset}
        limit={queryParams.limit}
        onChangePagination={noop}
        onRow={(r) => ({ onClick: () => setSelected(r._id), className: 'cursor-pointer' })}
        tableLayout="fixed"
        scroll={{ x: columns.reduce((s, col) => s + (col.width || 0), 0) }}
        size="small"
        headerChildren={
          <ListToolbar
            keys={KEYS}
            setQueryParams={setQueryParams}
            exportProps={{ company, kind: 'fiscalBills', filters: query, labels, disabled: !data?.count }}
          />
        }
      />
      {data && <Sums items={[[t('fiscalBills.sums.totalAmount'), money(data.sums.totalAmount)]]} />}
      <FiscalBillDetail company={company} id={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function FiscalBillDetail({ company, id, onClose }) {
  const { t } = useTranslation('sefState');
  const { data: d, isLoading, error } = useQuery({
    queryKey: ['sefState', company, 'fiscalBills', 'one', id],
    queryFn: () => API.sefState.getFiscalBillOne(company, id),
    enabled: Boolean(id),
  });
  const c = (key) => t(`fiscalBills.columns.${key}`);
  const raw = d?.raw || {};

  return (
    <Sidebar title={d?.number || t('fiscalBills.title')} open={Boolean(id)} onClose={onClose} className="max-w-xl">
      <div className="grid gap-4 p-4">
        {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {error && <Alert type="error" showIcon message={t('errors.load')} />}
        {d && (
          <>
            <Facts
              items={[
                [c('number'), d.number],
                [c('issuedAt'), dateTime(d.issuedAt)],
                [c('sellerTaxId'), d.sellerTaxId],
                [c('sellerName'), d.sellerName],
                [t('fiscalBills.detail.buyer'), [raw.BuyerName, raw.BuyerTin].filter(Boolean).join(' · ')],
                [c('totalAmount'), money(d.totalAmount)],
                [t('fiscalBills.invoiceCreated'), d.invoiceCreated ? t('status.yes') : t('status.no')],
                d.invoiceNumber && [c('invoiceNumber'), d.invoiceNumber],
                d.invoiceSentDate && [t('fiscalBills.detail.invoiceSentDate'), date(d.invoiceSentDate)],
              ]}
            />
            <p className="text-xs opacity-60">SEF Id {d.sefId}</p>
          </>
        )}
      </div>
    </Sidebar>
  );
}
